import { useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { TextBlock } from '../../Atoms/Text';

export interface DeleteViewDialogProps {
  // Null while the dialog is closed; the names of the reports in sight that hold the view otherwise, perhaps none.
  reports: string[] | null;
  // The user can reach reports the modal cannot list, because the schedules it reads belong to the selected accounts.
  otherAccounts: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const warning = (reports: string[], otherAccounts: boolean): string => {
  const consequence = 'a report left with no views is deleted.';
  if (reports.length === 0) {
    return `Deleting this view removes it from any report that holds it, in another account too, and ${consequence}`;
  }

  const names = reports.map((name) => `"${name}"`).join(', ');
  const [subject, them] = reports.length === 1 ? ['the report', 'that report'] : ['the reports', 'those reports'];
  const elsewhere = otherAccounts ? ' Reports in other accounts that hold it lose it too.' : '';
  const removal = `Deleting it removes it from ${them}, and ${consequence}`;
  return `This view is part of ${subject} ${names}. ${removal}${elsewhere}`;
};

export const DeleteViewDialog = ({ reports, otherAccounts, onCancel, onConfirm }: DeleteViewDialogProps) => {
  // The body stays while the dialog fades out, instead of flashing empty.
  const shown = useRef<string[]>([]);
  if (reports) shown.current = reports;

  return (
    <Dialog
      open={!!reports}
      onClose={onCancel}>
      <DialogTitle>Delete this view?</DialogTitle>
      <DialogContent>
        <TextBlock size="sm">{warning(shown.current, otherAccounts)}</TextBlock>
      </DialogContent>
      <DialogActions>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onCancel}>
          Cancel
        </button>
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={onConfirm}>
          Delete view
        </button>
      </DialogActions>
    </Dialog>
  );
};
